// Published number plate guides: one entry per finished /guides/{slug} page.
// Used by the /guides hub, the guide route titles/descriptions and the footer.
// KEEP IN SYNC with GUIDES in deploy/release/build-static.mjs and
// deploy/edge/site-request.js.

export interface Guide {
  slug: string;
  /** Page name: H1-level title and link text. */
  name: string;
  /** One line for the /guides hub. */
  summary: string;
  /** Meta description (150-160 characters). */
  description: string;
}

export const GUIDES_HUB_TITLE = 'Number Plate Guides | PlateX';
export const GUIDES_HUB_DESCRIPTION =
  'Plain English guides to UK number plates: dateless, suffix, prefix and current formats, cherished registrations, and how to transfer or retain a private plate.';

export const GUIDES: Guide[] = [
  {
    slug: 'number-plate-types',
    name: 'Number Plate Types',
    summary: 'How UK registration formats have changed since 1903.',
    description:
      "From the 1903 A1 to today's current style, see how UK number plate formats evolved: dateless, suffix, prefix and current registrations explained with examples.",
  },
  {
    slug: 'private-number-plates',
    name: 'Private Number Plates',
    summary: 'What private plates are, how they work and where they came from.',
    description:
      'What private number plates are, how they work and how they are assigned to a vehicle, plus a short history of UK private registrations and notable examples.',
  },
  {
    slug: 'cherished-number-plates',
    name: 'Cherished Number Plates',
    summary: 'What makes a registration cherished, with real examples.',
    description:
      'What makes a registration cherished? Learn the traits that give a UK private plate its value, from rare combinations to dateless status, with real examples.',
  },
  {
    slug: 'dateless-number-plates',
    name: 'Dateless Number Plates',
    summary: 'The oldest registrations, issued before 1963 with no year identifier.',
    description:
      'Dateless number plates were issued before 1963 and carry no year identifier, so they fit any vehicle. Learn their formats, rarity and what makes them valuable.',
  },
  {
    slug: 'prefix-number-plates',
    name: 'Prefix Number Plates',
    summary: 'The 1983 to 2001 format with the year letter first.',
    description:
      'Prefix number plates ran from 1983 to 2001 with the year letter first, as in A123 ABC. Learn the format, which cars can display them and where to find them.',
  },
  {
    slug: 'suffix-number-plates',
    name: 'Suffix Number Plates',
    summary: 'The 1963 to 1983 format with the year letter last.',
    description:
      'Suffix number plates ran from 1963 to 1983 with the year letter last, as in ABC 123A. Learn the format, which cars can display them and where to find them.',
  },
  {
    slug: 'how-to-transfer-a-registration',
    name: 'How to Transfer a Registration',
    summary: 'Move a private plate from one vehicle to another, step by step.',
    description:
      'A step by step guide to moving a private number plate from one vehicle to another in the UK: DVLA eligibility rules, applying online or by V317, and next steps.',
  },
  {
    slug: 'how-to-retain-a-registration',
    name: 'How to Retain a Registration',
    summary: 'Keep a private plate on a retention certificate between vehicles.',
    description:
      'How to take a private number plate off a vehicle and keep it on a V778 retention certificate: DVLA eligibility rules, the £80 fee and renewing every 10 years.',
  },
  {
    slug: 'how-to-save-money',
    name: 'How to Save Money',
    summary: 'Compare prices for the same plate and pay less.',
    description:
      'How PlateX price comparison helps you pay less for a private number plate by comparing dealer, DVLA and private seller prices for the same registration at once.',
  },
];

export const guideTitle = (name: string): string => `${name} | Number Plate Guides | PlateX`;

export const guideBySlug = (slug: string): Guide => GUIDES.find((g) => g.slug === slug) as Guide;
